import React from 'react';
import {
    GalleryMasonry,
    ArtistSpotlight,
    SpacesCalendar,
    ContentHub,
    DAOBadges
} from '../components/curator';
import '../themes/curator.css';

// ============================================
// Type Definitions
// ============================================
interface GalleryItem {
    id: string;
    image: string;
    title: string;
    artist?: string;
    collection?: string;
    link?: string;
}

interface SpaceEvent {
    id: string;
    title: string;
    date: Date;
    coHosts?: { name: string; avatar: string; handle: string }[];
    spaceLink?: string;
}

interface ContentCard {
    type: 'newsletter' | 'podcast' | 'discord' | 'youtube';
    title: string;
    description: string;
    icon: string;
    cta: string;
    link: string;
    stats?: string;
}

export interface CuratorTemplateData {
    identity: {
        name: string;
        handle: string;
        pfp: string;
        tagline: string;
        bio?: string;
    };
    daoBadges: { name: string; logo: string; link: string }[];
    collection: {
        title: string;
        items: GalleryItem[];
    };
    artistSpotlight?: {
        label?: string;
        artist: { name: string; handle: string; avatar: string; bio: string };
        featuredWork: { image: string; title: string };
        ctaLabel?: string;
        ctaLink: string;
    };
    spaces: SpaceEvent[];
    contentLinks: ContentCard[];
    social: {
        twitter?: string;
        discord?: string;
        instagram?: string;
        youtube?: string;
    };
    theme?: {
        backgroundArt?: string;
    };
}

// ============================================
// Social Icons Component
// ============================================
const SocialIcon: React.FC<{ platform: string; href: string }> = ({ platform, href }) => {
    const icons: Record<string, string> = {
        twitter: '𝕏',
        discord: '💬',
        instagram: '📷',
        youtube: '▶️',
    };

    return (
        <a href={href} target="_blank" rel="noopener noreferrer" title={platform}>
            {icons[platform] || '🔗'}
        </a>
    );
};

// ============================================
// Main Template Component
// ============================================
export const CuratorTemplate: React.FC<{ data: CuratorTemplateData }> = ({ data }) => {
    const { identity, daoBadges, collection, artistSpotlight, spaces, contentLinks, social, theme } = data;

    return (
        <div className="curator-layout" data-theme="curator">
            {/* Header */}
            <header className="curator-header">
                <div className="curator-header-logo">{identity.handle}</div>

                <nav className="curator-header-nav">
                    <a href="#collection">Collection</a>
                    <a href="#spaces">Spaces</a>
                    <a href="#about">About</a>
                </nav>

                <a
                    href={social.twitter || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="curator-header-cta"
                >
                    Follow
                </a>
            </header>

            {/* Hero Section */}
            <section className="curator-hero">
                {theme?.backgroundArt && (
                    <div className="curator-hero-bg">
                        <img src={theme.backgroundArt} alt="" />
                    </div>
                )}

                <div className="curator-hero-content">
                    <img
                        src={identity.pfp}
                        alt={identity.name}
                        className="curator-hero-pfp"
                    />
                    <h1 className="curator-hero-name">{identity.name}</h1>
                    <div className="curator-hero-handle">{identity.handle}</div>
                    <p className="curator-hero-tagline">{identity.tagline}</p>

                    {daoBadges.length > 0 && <DAOBadges badges={daoBadges} />}
                </div>
            </section>

            {/* Collection Gallery */}
            <section id="collection" className="curator-section">
                <h2 className="curator-section-title">{collection.title}</h2>
                <GalleryMasonry items={collection.items} columns={4} />
            </section>

            {/* Artist Spotlight */}
            {artistSpotlight && (
                <section className="curator-section">
                    <ArtistSpotlight
                        label={artistSpotlight.label}
                        artist={artistSpotlight.artist}
                        featuredWork={artistSpotlight.featuredWork}
                        ctaLabel={artistSpotlight.ctaLabel}
                        ctaLink={artistSpotlight.ctaLink}
                    />
                </section>
            )}

            {/* Upcoming Spaces */}
            {spaces.length > 0 && (
                <section id="spaces" className="curator-section">
                    <h2 className="curator-section-title">Upcoming Spaces</h2>
                    <SpacesCalendar events={spaces} />
                </section>
            )}

            {/* Content Links */}
            {contentLinks.length > 0 && (
                <section className="curator-section">
                    <h2 className="curator-section-title">Connect</h2>
                    <ContentHub cards={contentLinks} />
                </section>
            )}

            {/* Footer */}
            <footer className="curator-footer">
                <div className="curator-footer-social">
                    {social.twitter && <SocialIcon platform="twitter" href={social.twitter} />}
                    {social.discord && <SocialIcon platform="discord" href={social.discord} />}
                    {social.instagram && <SocialIcon platform="instagram" href={social.instagram} />}
                    {social.youtube && <SocialIcon platform="youtube" href={social.youtube} />}
                </div>
                <p className="curator-footer-text">
                    © {new Date().getFullYear()} {identity.name} • Built with Crypto Factory
                </p>
            </footer>
        </div>
    );
};

export default CuratorTemplate;
